/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The form value an input collects. label is submitted as an admission's name.
 */
export const Field = {
  Unknown: "",
  Name: "name",
  Description: "description",
  Issuer: "issuer",
  JwksUri: "jwks_uri",
  Subject: "subject",
  Label: "label",
} as const;
/**
 * The form value an input collects. label is submitted as an admission's name.
 */
export type Field = OpenEnum<typeof Field>;

/**
 * The dashboard validator an input's value must pass. The server applies the same rules when the form is submitted.
 */
export const Format = {
  Unknown: "",
  IssuerUrl: "issuer_url",
  JwksUri: "jwks_uri",
  PlatformName: "platform_name",
  PlatformDescription: "platform_description",
  SubjectRule: "subject_rule",
  None: "none",
} as const;
/**
 * The dashboard validator an input's value must pass. The server applies the same rules when the form is submitted.
 */
export type Format = OpenEnum<typeof Format>;

/**
 * The kind of content. wildcard_caution marks where the form warns that a wildcard rule admits more than one identity; the dashboard writes that warning, since it names the rule and the agent.
 */
export const WorkloadFormBlockType = {
  Text: "text",
  Link: "link",
  Input: "input",
  AgentPicker: "agent_picker",
  Tags: "tags",
  WildcardCaution: "wildcard_caution",
} as const;
/**
 * The kind of content. wildcard_caution marks where the form warns that a wildcard rule admits more than one identity; the dashboard writes that warning, since it names the rule and the agent.
 */
export type WorkloadFormBlockType = OpenEnum<typeof WorkloadFormBlockType>;

/**
 * One piece of a custom flow's form. Which fields are set depends on type; the rest are empty.
 */
export type WorkloadFormBlock = {
  /**
   * The form value an input collects. label is submitted as an admission's name.
   */
  field: Field;
  /**
   * The dashboard validator an input's value must pass. The server applies the same rules when the form is submitted.
   */
  format: Format;
  /**
   * Markdown shown under a form control while it has no validation message. Raw HTML in it must not be rendered.
   */
  help: string;
  /**
   * A link's https target.
   */
  href: string;
  /**
   * A link's label, or a form control's.
   */
  label: string;
  /**
   * A text block's Markdown. Raw HTML in it must not be rendered.
   */
  markdown: string;
  /**
   * Whether an input is a text area.
   */
  multiline: boolean;
  /**
   * Shown in an empty form control.
   */
  placeholder: string;
  /**
   * Whether an input shows its value without letting it change. A read-only value is not submitted.
   */
  readOnly: boolean;
  /**
   * The kind of content. wildcard_caution marks where the form warns that a wildcard rule admits more than one identity; the dashboard writes that warning, since it names the rule and the agent.
   */
  type: WorkloadFormBlockType;
};

/** @internal */
export const Field$inboundSchema: z.ZodMiniType<Field, unknown> = openEnums
  .inboundSchema(Field);

/** @internal */
export const Format$inboundSchema: z.ZodMiniType<Format, unknown> = openEnums
  .inboundSchema(Format);

/** @internal */
export const WorkloadFormBlockType$inboundSchema: z.ZodMiniType<
  WorkloadFormBlockType,
  unknown
> = openEnums.inboundSchema(WorkloadFormBlockType);

/** @internal */
export const WorkloadFormBlock$inboundSchema: z.ZodMiniType<
  WorkloadFormBlock,
  unknown
> = z.pipe(
  z.object({
    field: Field$inboundSchema,
    format: Format$inboundSchema,
    help: z.string(),
    href: z.string(),
    label: z.string(),
    markdown: z.string(),
    multiline: z.boolean(),
    placeholder: z.string(),
    read_only: z.boolean(),
    type: WorkloadFormBlockType$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "read_only": "readOnly",
    });
  }),
);

export function workloadFormBlockFromJSON(
  jsonString: string,
): SafeParseResult<WorkloadFormBlock, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => WorkloadFormBlock$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'WorkloadFormBlock' from JSON`,
  );
}
